import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import SchemaOrg from '@/components/feature/SchemaOrg';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import DramaCard from '@/pages/home/components/DramaCard';
import { getDramaById, getDramasByCategory } from '@/mocks/dramas';
import { getArticleById, getRelatedArticles, getFeaturedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const drama = id ? getDramaById(id) : undefined;
  const article = id ? getArticleById(id) : undefined;

  if (drama) {
    const cat = categories.find((c) => c.slug === drama.category);
    const relatedDramas = getDramasByCategory(drama.category).filter((d) => d.id !== drama.id).slice(0, 6);

    return (
      <>
        <SchemaOrg type="Article" data={{
          headline: drama.title,
          description: drama.description,
          image: drama.coverImage,
          datePublished: drama.releaseDate,
          author: { '@type': 'Person', name: drama.director },
          publisher: { '@type': 'Organization', name: '星辰影院' },
        }} />
        <SEOHead
          title={`${drama.title} - ${drama.originalTitle} | 星辰影院`}
          description={`${drama.title}：${drama.description} 导演：${drama.director}，主演：${drama.cast.join('、')}，星辰影院免费高清在线观看。`}
          keywords={`星辰影院,${drama.title},${drama.genres.join(',')},${drama.region},免费电视剧,高清在线观看`}
          canonicalUrl={`/article/${drama.id}`}
          ogImage={drama.coverImage}
        />

        <main className="min-h-screen pt-20 pb-12">
          <article className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: cat?.name || '分类', href: `/category/${drama.category}` },
                { label: drama.title },
              ]} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 mb-12">
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="relative overflow-hidden rounded-xl aspect-[2/3] mb-4">
                    <img
                      src={drama.coverImage}
                      alt={drama.title}
                      title={`${drama.title} - 星辰影院免费电视剧`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      {drama.isHot && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                          热播
                        </span>
                      )}
                      {drama.isNew && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                          新剧
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-2">
                  {drama.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-5">{drama.originalTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-primary-500/10 text-primary-400">
                    <i className="ri-star-fill w-3 h-3 flex items-center justify-center"></i>
                    {drama.rating}
                  </span>
                  <span className="text-xs text-foreground-400">{drama.year}</span>
                  <span className="text-foreground-600">·</span>
                  <span className="text-xs text-foreground-400">{drama.region}</span>
                  <span className="text-foreground-600">·</span>
                  <span className="text-xs text-foreground-400">{drama.episodes}集</span>
                  <span className="text-foreground-600">·</span>
                  <span className="text-xs text-foreground-400">{drama.status}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {drama.genres.map((genre) => (
                    <span key={genre} className="px-3 py-1 rounded-full text-xs bg-background-100 border border-background-200/60 text-foreground-400 whitespace-nowrap">
                      {genre}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 p-5 rounded-xl bg-background-100/70 border border-background-200/60">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm text-foreground-200 font-medium mt-0.5">{drama.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm text-foreground-200 font-medium mt-0.5">{drama.cast.join('、')}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">首播日期</span>
                    <p className="text-sm text-foreground-200 font-medium mt-0.5">{drama.releaseDate}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">更新状态</span>
                    <p className="text-sm text-foreground-200 font-medium mt-0.5">{drama.updateInfo}</p>
                  </div>
                </div>

                <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-3">剧情简介</h2>
                <div className="text-sm text-foreground-400 leading-relaxed space-y-3">
                  {drama.longDescription.split('\n').filter(Boolean).map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>

            {relatedDramas.length > 0 && (
              <section className="border-t border-background-200/60 pt-10 md:pt-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
                  更多{cat?.name || '相关'}推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedDramas.slice(0, 6).map((d) => (
                    <DramaCard key={d.id} drama={d} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
      </>
    );
  }

  if (article) {
    const relatedArticles = getRelatedArticles(article.id, 3);

    return (
      <>
        <SchemaOrg type="Article" data={{
          headline: article.title,
          description: article.summary,
          image: article.coverImage,
          datePublished: article.publishDate,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: '星辰影院' },
          wordCount: article.content.length,
        }} />
        <SEOHead
          title={`${article.title} | 星辰影院`}
          description={article.summary}
          keywords={`星辰影院,${article.tags.join(',')},追剧攻略`}
          canonicalUrl={`/article/${article.id}`}
          ogImage={article.coverImage}
        />

        <main className="min-h-screen pt-20 pb-12">
          <article className="max-w-[900px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: article.categoryName },
                { label: article.title },
              ]} />
            </div>

            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500/10 text-primary-400 mb-4 whitespace-nowrap">
              {article.categoryName}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-6 flex-wrap">
              <span>{article.author}</span>
              <span className="text-foreground-600">·</span>
              <span>{article.publishDate}</span>
              <span className="text-foreground-600">·</span>
              <span>{article.readCount.toLocaleString()} 阅读</span>
            </div>

            <div className="rounded-xl overflow-hidden mb-8 aspect-[16/9]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-300 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/60">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-100 border border-background-200/60 text-foreground-400 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/60">
                <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-5">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group block rounded-xl overflow-hidden bg-background-100 border border-background-200/60 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-sm font-heading font-semibold text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
      </>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="text-center">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
          <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
        </div>
        <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-2">内容未找到</h2>
        <p className="text-sm text-foreground-500 mb-4">该内容不存在或已被移除</p>
        <Link
          to="/"
          className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    </div>
  );
}